import './grn-factory-check.scss';

import { Checkbox, type CheckboxChangeEvent, notification } from 'antd';
import { useEffect, useState } from 'react';

import { Button } from '@/components/Shared/ui';
import { EGrnFactoryCheckResult } from '@/constants';
import { autoCreditApi } from '@/integrations/app.api/auto-credit.api';
import { LoadingOutlined, WarningOutlined } from '@/media/antd-icons';
import SuccessIcon from '@/media/svg/success.svg?react';
import { handleError } from '@/services/handleError';
import { useAppDispatch, useAppSelector } from '@/store';
import { setIsGrnFactoryCheckPassed } from '@/store/financingStep.slice';

import useRefreshOrderData from '../../../hooks/useRefreshOrderData';

type TProps = {
  grnFileLink: string;
  grnFileType: string;
};

const CHECK_MANUALLY_TEXT =
  'Проверьте БИН и юридическое название получателя в ГП. Чтобы продолжить работу с заявкой, подтвердите корректность документа вручную.';
const START_CHECK_TEXT =
  'Запустите автоматическую проверку данных завода в ГП, чтобы продолжить работу с заявкой.';
const RETRY_CHECK_TEXT =
  'Попробуйте повторно запустить проверку данных завода в ГП.';

const TWO_MINUTES = 2 * 60 * 1000;
const currentTimeMinusTwoMinutes = new Date(Date.now() - TWO_MINUTES);

const GrnFactoryCheck: React.FC<TProps> = ({ grnFileLink, grnFileType }) => {
  const dispatch = useAppDispatch();

  const { orderApp } = useAppSelector((state) => state.newOrder);
  const { selectedBank } = useAppSelector((state) => state.financingStep);
  const { factoryCheck } = selectedBank || {};
  const {
    allowManualConfirmation = false,
    lastStartedAt = '',
    manuallyConfirmed = false,
    reason = null,
    result = null,
    startCount = 0
  } = factoryCheck || {};

  const { refreshOrderData } = useRefreshOrderData({
    allowWithOrder: true,
    autoFetch: false
  });

  const [isStartLoading, setIsStartLoading] = useState(false);

  const isFinanced: boolean = selectedBank?.status.code === 'financed';

  const isTwoMinutesPassed: boolean =
    !!lastStartedAt && new Date(lastStartedAt) < currentTimeMinusTwoMinutes;

  const isManualStartRequired: boolean = !result;

  const isLimitHasBeenExceeded: boolean = startCount >= 3;

  const isManualCheckRequired: boolean =
    isLimitHasBeenExceeded && isTwoMinutesPassed;

  const isRetryCheckAvailable: boolean =
    !isLimitHasBeenExceeded && isTwoMinutesPassed;

  const isPending: boolean = result === EGrnFactoryCheckResult.PENDING;

  const isSkippedSapa: boolean = result === EGrnFactoryCheckResult.SKIPPED_SAPA;
  const isSkipped: boolean =
    isSkippedSapa || result === EGrnFactoryCheckResult.SKIPPED;

  const isMismatched: boolean = result === EGrnFactoryCheckResult.MISMATCHED;
  const isFailed: boolean =
    isMismatched || result === EGrnFactoryCheckResult.UNABLE_TO_CHECK;

  const isPassed: boolean =
    result === EGrnFactoryCheckResult.MATCHED ||
    result === EGrnFactoryCheckResult.MANUALLY_CONFIRMED;

  const startGrnFactoryCheck = async () => {
    if (!orderApp || !selectedBank) return;

    try {
      setIsStartLoading(true);
      await autoCreditApi.startGrnFactoryCheck({
        bankCode: selectedBank.bank.name,
        fileType: grnFileType,
        gpId: grnFileLink,
        requestId: orderApp.uuid,
        responseId: selectedBank.id
      });

      notification.success({
        message: 'Проверка успешно запущена'
      });

      try {
        await refreshOrderData();
      } catch (error) {
        handleError(error, {
          customTitle: 'Произошла ошибка при обновлении заявки'
        });
      }
    } catch (error) {
      handleError(error, {
        customTitle: 'Произошла ошибка при запуске проверки'
      });
    } finally {
      setIsStartLoading(false);
    }
  };

  useEffect(() => {
    if (!selectedBank || isFinanced) return;
    dispatch(setIsGrnFactoryCheckPassed(isPassed));
    return () => {
      dispatch(setIsGrnFactoryCheckPassed(true));
    };
  }, [selectedBank, isFinanced, isPassed]);

  if (!selectedBank || isFinanced) return null;

  if (isManualStartRequired) {
    return (
      <div className="grn-factory-check grn-factory-check--warning">
        <div className="grn-factory-check__content">
          <div className="grn-factory-check__icon-wrapper">
            <WarningOutlined />
          </div>
          <div className="grn-factory-check__text-wrapper">
            <h5 className="grn-factory-check__title">
              Автоматическая проверка данных завода в ГП не запущена
            </h5>
            <span className="grn-factory-check__text">{START_CHECK_TEXT}</span>
          </div>
        </div>

        <div className="grn-factory-check__footer">
          <Button
            type="button"
            size="small"
            variant="filled-primary"
            onClick={startGrnFactoryCheck}
            isLoading={isStartLoading}
          >
            {isStartLoading ? 'Запускается проверка...' : 'Запустить проверку'}
          </Button>
        </div>
      </div>
    );
  }

  if (isPending)
    return (
      <div className="grn-factory-check grn-factory-check--loading">
        <div className="grn-factory-check__content">
          <div className="grn-factory-check__icon-wrapper">
            <LoadingOutlined />
          </div>
          <div className="grn-factory-check__text-wrapper">
            <span className="grn-factory-check__text">
              {isManualCheckRequired
                ? 'Лимит попыток проверки данных завода в ГП превышен. Пожалуйста, проверьте данные вручную.'
                : isRetryCheckAvailable
                  ? 'Проверка еще в процессе, но пока не вернула результат.'
                  : 'Проверяем данные завода в гарантийном письме...'}
            </span>
          </div>
        </div>

        {isManualCheckRequired ? (
          <ConfirmFooter />
        ) : (
          isRetryCheckAvailable && (
            <RetryFooter
              onRetry={startGrnFactoryCheck}
              isLoading={isStartLoading}
            />
          )
        )}
      </div>
    );

  if (isSkipped) {
    const titleDefault = isSkippedSapa
      ? 'Для SAPA автоматическая проверка завода не выполняется'
      : 'Не удалось автоматически проверить данные завода';

    return (
      <div className="grn-factory-check grn-factory-check--skipped">
        <div className="grn-factory-check__content">
          <div className="grn-factory-check__icon-wrapper">
            <WarningOutlined />
          </div>
          <div className="grn-factory-check__text-wrapper">
            <h5 className="grn-factory-check__title">
              {reason || titleDefault}
            </h5>
            {allowManualConfirmation && (
              <span className="grn-factory-check__text">
                {CHECK_MANUALLY_TEXT}
              </span>
            )}
          </div>
        </div>

        {allowManualConfirmation && <ConfirmFooter />}
      </div>
    );
  }

  if (isFailed) {
    const titleDefault = isMismatched
      ? 'Данные завода в гарантийном письме не совпадают'
      : 'Не удалось автоматически проверить данные завода';

    return (
      <div className="grn-factory-check grn-factory-check--warning">
        <div className="grn-factory-check__content">
          <div className="grn-factory-check__icon-wrapper">
            <WarningOutlined />
          </div>
          <div className="grn-factory-check__text-wrapper">
            <h5 className="grn-factory-check__title">
              {reason || titleDefault}
            </h5>
            {allowManualConfirmation && (
              <span className="grn-factory-check__text">
                {CHECK_MANUALLY_TEXT}
              </span>
            )}
          </div>
        </div>

        {allowManualConfirmation && <ConfirmFooter />}
      </div>
    );
  }

  if (isPassed) {
    const title = manuallyConfirmed
      ? 'Гарантийное письмо проверено вручную'
      : 'Гарантийное письмо проверено';

    return (
      <div className="grn-factory-check grn-factory-check--matched">
        <div className="grn-factory-check__content">
          <div className="grn-factory-check__icon-wrapper">
            <SuccessIcon />
          </div>
          <div className="grn-factory-check__text-wrapper">
            <h5 className="grn-factory-check__title">{title}</h5>
            {!manuallyConfirmed && !!reason && (
              <span className="grn-factory-check__text">{reason}</span>
            )}
          </div>
        </div>
      </div>
    );
  }

  return null;
};

const ConfirmFooter: React.FC = () => {
  const { selectedBank } = useAppSelector((state) => state.financingStep);

  const { refreshOrderData } = useRefreshOrderData({
    allowWithOrder: true,
    autoFetch: false
  });

  const [isManualConfirmation, setIsManualConfirmation] = useState(false);
  const [isManualConfirmLoading, setIsManualConfirmLoading] = useState(false);

  const handleCheck = (e: CheckboxChangeEvent) => {
    setIsManualConfirmation(e.target.checked);
  };

  const handleConfirm = async () => {
    if (!selectedBank) return;
    try {
      setIsManualConfirmLoading(true);
      await autoCreditApi.confirmGrnFactoryCheck(selectedBank.id);

      notification.success({
        message: 'Проверка успешно подтверждена'
      });

      try {
        await refreshOrderData();
      } catch (error) {
        handleError(error, {
          customTitle: 'Произошла ошибка при обновлении заявки'
        });
      }
    } catch (error) {
      handleError(error, {
        customTitle: 'Произошла ошибка при подтверждении проверки'
      });
    } finally {
      setIsManualConfirmLoading(false);
    }
  };

  return (
    <div className="grn-factory-check__footer">
      <Checkbox
        checked={isManualConfirmation}
        onChange={handleCheck}
        disabled={isManualConfirmLoading}
        classNames={{ label: 'grn-factory-check__checkbox-label' }}
      >
        Я проверил(а) ГП и подтверждаю, что данные корректны
      </Checkbox>
      <Button
        type="button"
        size="small"
        variant="filled-primary"
        onClick={handleConfirm}
        disabled={!isManualConfirmation}
        isLoading={isManualConfirmLoading}
      >
        {isManualConfirmLoading
          ? 'Выполняется подтверждение...'
          : 'Подтвердить проверку'}
      </Button>
    </div>
  );
};

const RetryFooter: React.FC<{ onRetry: () => void; isLoading: boolean }> = ({
  onRetry,
  isLoading
}) => {
  return (
    <div className="grn-factory-check__footer">
      <span className="grn-factory-check__text">{RETRY_CHECK_TEXT}</span>
      <Button
        type="button"
        size="small"
        variant="filled-primary"
        onClick={onRetry}
        isLoading={isLoading}
      >
        {isLoading ? 'Запускается...' : 'Попробовать снова'}
      </Button>
    </div>
  );
};

export default GrnFactoryCheck;
